import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";

import "./Login.css";

import ajb from "./images/ajb.png";

import useAxios from "./useAxios";
import Select from "react-select";

import PinPad from "./PinPad";

// Function to cache image data as base64 to eliminate network requests and flashing
const cacheImageData = async (imageUrl) => {
  try {
    const response = await fetch(imageUrl);
    const blob = await response.blob();
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64data = reader.result;
      localStorage.setItem(`cachedImage_${imageUrl}`, base64data);
    };
    reader.readAsDataURL(blob);
  } catch (error) {
    console.error("Error caching image data:", error);
  }
};

// Function to get cached image data
const getCachedImageData = (imageUrl) => {
  try {
    return localStorage.getItem(`cachedImage_${imageUrl}`);
  } catch (error) {
    console.error("Error reading cached image data:", error);
    return null;
  }
};

// Function to get ANY cached tenant image (immediate display without API dependency)
const getAnyCachedTenantImage = () => {
  try {
    const cachedKeys = Object.keys(localStorage).filter((key) =>
      key.startsWith("cachedImage_")
    );
    if (cachedKeys.length > 0) {
      const cachedImageData = localStorage.getItem(cachedKeys[0]);
      return cachedImageData;
    }
    return null;
  } catch (error) {
    console.error("Error reading any cached image data:", error);
    return null;
  }
};

const loginSchema = Yup.object().shape({
  username: Yup.string().required("Username is required"),
  password: Yup.string().required("Password is required"),
});

const Login = ({ onLogin }) => {
  const navigate = useNavigate();
  const { matchId } = useParams();

  const [tenantIcon, setTenantIcon] = useState(null);
  const [version, setVersion] = useState(null);
  const [isButtonDisabled, setButtonDisabled] = useState(false);
  const [error, setError] = useState(false);
  const [showTraditionalLogin, setShowTraditionalLogin] = useState(false);
  // Use useState for rememberedUser and read from localStorage directly
  const [rememberedUser, setRememberedUser] = useState(() => {
    try {
      const stored = localStorage.getItem("rememberedOidcUser");
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [isLandscape, setIsLandscape] = useState(
    window.innerWidth > window.innerHeight
  );

  const [axios] = useAxios();
  const [loginProcessed, setLoginProcessed] = useState(false);
  const loginCalledRef = useRef(false);

  // Update rememberedUser state when component mounts to ensure we have latest localStorage data
  useEffect(() => {
    try {
      const stored = localStorage.getItem("rememberedOidcUser");
      const parsed = stored ? JSON.parse(stored) : null;
      setRememberedUser(parsed);
    } catch (error) {
      console.error("Error reading from localStorage:", error);
    }
  }, []);

  useEffect(() => {
    // Check for token in URL parameters (from Google Auth callback)
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get("token");
    const tenantId = urlParams.get("tenant");
    const versionParam = urlParams.get("version");
    const redirectParam = urlParams.get("redirect");

    if (token && tenantId && !loginProcessed) {
      setLoginProcessed(true);

      // If we have a redirect parameter, store it back in sessionStorage
      if (redirectParam) {
        sessionStorage.setItem("redirectAfterLogin", redirectParam);
      }
      // Store user information for future logins
      try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        if (payload.email) {
          const userInfo = {
            email: payload.email,
            name: payload.name || payload.unique_name,
            lastLogin: Date.now(),
          };
          // Use localStorage directly to avoid React state update timing issues
          localStorage.setItem("rememberedOidcUser", JSON.stringify(userInfo));
          setRememberedUser(userInfo);
        }
      } catch (error) {
        console.error("Error decoding token for user storage:", error);
      }

      // Fetch full tenant info before calling onLogin
      axios.get(
        "public/tenant-info",
        (tenantData) => {
          const tenantInfo = {
            id: tenantId,
            icon: tenantData.icon,
            name: tenantData.name,
            super: tenantData.super,
            remoteAccessHelpUrl: tenantData.remoteAccessHelpUrl,
          };

          // Cache tenant info in localStorage for instant loading next time
          localStorage.setItem("cachedTenantInfo", JSON.stringify(tenantInfo));

          // Cache the actual image data to prevent network requests and eliminate flash
          if (tenantData.icon) {
            cacheImageData(tenantData.icon);
          }

          if (!loginCalledRef.current) {
            loginCalledRef.current = true;
            onLogin({
              token: token,
              tenant: tenantInfo,
              version: versionParam,
            });
          }
          // Clean up URL
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname
          );
        },
        (error) => {
          console.error("Error fetching tenant info:", error);

          // Try to use cached tenant info if available
          const cachedTenantInfo = localStorage.getItem("cachedTenantInfo");
          let fallbackTenant = { id: tenantId };

          if (cachedTenantInfo) {
            try {
              const parsed = JSON.parse(cachedTenantInfo);
              fallbackTenant = {
                id: tenantId,
                icon: parsed.icon,
                name: parsed.name,
              };
            } catch (parseError) {
              console.error("Error parsing cached tenant info:", parseError);
            }
          }

          if (!loginCalledRef.current) {
            loginCalledRef.current = true;
            onLogin({
              token: token,
              tenant: fallbackTenant,
              version: versionParam,
            });
          }
          // Clean up URL
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname
          );
        }
      );
      return;
    }

    axios.get("public/tenant-info", (data) => {
      setTenantIcon(data.icon);
      setVersion(data.version);

      // Cache tenant info for instant loading
      const tenantInfo = {
        icon: data.icon,
        name: data.name,
      };
      localStorage.setItem("cachedTenantInfo", JSON.stringify(tenantInfo));

      // Cache the actual image data to prevent network requests
      if (data.icon) {
        cacheImageData(data.icon);
      }
    });
  }, [axios, onLogin, loginProcessed]);

  // Fetch tables when matchId is not provided (table selection mode)
  useEffect(() => {
    if (!matchId) {
      const fetchTables = async () => {
        try {
          const apiUrl = process.env.REACT_APP_API_URL || "";
          const baseUrl = apiUrl.replace(/\/+$/, "");
          const response = await fetch(`${baseUrl}/public/tables`, {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Origin: window.location.origin,
            },
          });

          if (response.ok) {
            const data = await response.json();
            setTables(data);
          } else {
            console.error("Failed to fetch tables:", response.status);
          }
        } catch (error) {
          console.error("Error fetching tables:", error);
        }
      };

      fetchTables();
    }
  }, [matchId]);

  // Handle orientation changes
  useEffect(() => {
    const handleResize = () => {
      setIsLandscape(window.innerWidth > window.innerHeight);
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
    };
  }, []);

  const handleError = (error) => {
    setError(error);
    setButtonDisabled(false);
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    setError(null);
    setButtonDisabled(true);
    setTimeout(() => {
      axios.post("auth/login", values, onLogin, handleError);
      setSubmitting(false);
    }, 1000);
  };

  const handleGoogleLogin = () => {
    const apiUrl =
      process.env.REACT_APP_API_URL || "http://localhost:8080/api/";
    const baseUrl = apiUrl.replace("/api/", "");

    // Check for redirect path and include it in the returnUrl
    const redirectPath = sessionStorage.getItem("redirectAfterLogin");
    let returnUrl = `${window.location.origin}/login`;

    if (redirectPath) {
      // Encode the redirect path as a URL parameter
      returnUrl += `?redirect=${encodeURIComponent(redirectPath)}`;
    }

    let authUrl = `${baseUrl}/api/auth/external-login?provider=Google&returnUrl=${encodeURIComponent(
      returnUrl
    )}`;

    // Add login_hint if we have a remembered user
    if (rememberedUser && rememberedUser.email) {
      authUrl += `&login_hint=${encodeURIComponent(rememberedUser.email)}`;
    }

    window.location.href = authUrl;
  };

  const handleFacebookLogin = () => {
    const apiUrl =
      process.env.REACT_APP_API_URL || "http://localhost:8080/api/";
    const baseUrl = apiUrl.replace("/api/", "");

    // Check for redirect path and include it in the returnUrl
    const redirectPath = sessionStorage.getItem("redirectAfterLogin");
    let returnUrl = `${window.location.origin}/login`;

    if (redirectPath) {
      // Encode the redirect path as a URL parameter
      returnUrl += `?redirect=${encodeURIComponent(redirectPath)}`;
    }

    const authUrl = `${baseUrl}/api/auth/external-login?provider=Facebook&returnUrl=${encodeURIComponent(
      returnUrl
    )}`;

    window.location.href = authUrl;
  };

  const handlePinSubmit = (pin, onSuccess, onError) => {
    if (matchId) {
      // Direct match login with specific matchId
      axios.post(
        "auth/match-login",
        { matchId: parseInt(matchId), pin },
        onLogin,
        onError
      );
    } else {
      // Table-based match login - lookup matchId by table
      if (!selectedTable) {
        onError("Please select a table");
        return;
      }
      axios.post(
        "auth/table-match-login",
        { tableId: selectedTable.id, pin },
        onLogin,
        onError
      );
    }
  };

  // If matchId is present OR we're on the match login route, show PIN pad for match login
  if (matchId || window.location.pathname.startsWith("/login/match")) {
    return (
      <div className="login-container" style={{ height: "auto" }}>
        <div
          className={`login-form ${isLandscape ? "match-login-landscape" : ""}`}
        >
          <div className="login-header">
            {(() => {
              // First check for any cached tenant images (immediate display)
              const anyCachedImage = getAnyCachedTenantImage();
              if (anyCachedImage) {
                return (
                  <img
                    style={{ width: "20vh" }}
                    src={anyCachedImage}
                    alt="Tenant Logo"
                  />
                );
              }

              // Then check if we have tenantIcon from API with cached data for it
              if (tenantIcon) {
                const specificCachedImage = getCachedImageData(tenantIcon);
                return (
                  <img
                    style={{ width: "20vh" }}
                    src={specificCachedImage || tenantIcon}
                    alt="Tenant Logo"
                  />
                );
              }

              // Fallback to AJB logo
              return <img style={{ width: "20vh" }} src={ajb} alt="AJB Logo" />;
            })()}
          </div>

          <div className="match-login-content">
            {/* Table selection - only show when matchId is not provided */}
            {!matchId && (
              <div className="table-selection-container">
                <Select
                  value={selectedTable}
                  onChange={setSelectedTable}
                  options={tables}
                  getOptionValue={(option) => option.id}
                  getOptionLabel={(option) => option.name}
                  placeholder="Choose a table..."
                  isSearchable={false}
                  classNamePrefix="react-select"
                  styles={{
                    control: (provided, state) => ({
                      ...provided,
                      minHeight: "44px",
                      fontSize: "16px",
                      border: `2px solid ${
                        state.isFocused ? "#1a7e59" : "#e1e5e9"
                      }`,
                      borderRadius: "8px",
                      backgroundColor: state.isFocused ? "#fff" : "#f8f9fa",
                      boxShadow: state.isFocused
                        ? "0 0 0 3px rgba(26, 126, 89, 0.1)"
                        : "none",
                      "&:hover": {
                        borderColor: "#1a7e59",
                      },
                    }),
                    option: (provided, state) => ({
                      ...provided,
                      backgroundColor: state.isSelected
                        ? "#1a7e59"
                        : state.isFocused
                        ? "#f8f9fa"
                        : "white",
                      color: state.isSelected ? "white" : "#495057",
                      fontSize: "16px",
                      padding: "12px 16px",
                    }),
                    placeholder: (provided) => ({
                      ...provided,
                      color: "#6c757d",
                      fontSize: "16px",
                    }),
                    singleValue: (provided) => ({
                      ...provided,
                      color: "#495057",
                      fontSize: "16px",
                    }),
                  }}
                />
              </div>
            )}

            <PinPad onPinSubmit={handlePinSubmit} />

            {version && (
              <div className="version-info">
                <img style={{ width: "5vh" }} src={ajb} alt="AJB Logo" />
                <span>{version}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-form">
        <div className="login-header">
          {(() => {
            // First check for any cached tenant images (immediate display)
            const anyCachedImage = getAnyCachedTenantImage();
            if (anyCachedImage) {
              return (
                <img
                  style={{ width: "30vh" }}
                  src={anyCachedImage}
                  alt="Tenant Logo"
                />
              );
            }

            // Then check if we have tenantIcon from API with cached data for it
            if (tenantIcon) {
              const specificCachedImage = getCachedImageData(tenantIcon);
              return (
                <img
                  style={{ width: "30vh" }}
                  src={specificCachedImage || tenantIcon}
                  alt="Tenant Logo"
                />
              );
            }

            // Fallback to AJB logo
            return <img style={{ width: "30vh" }} src={ajb} alt="AJB Logo" />;
          })()}
        </div>
        {/* Social Login Buttons */}
        <div className="social-login-section">
          <button
            type="button"
            onClick={handleGoogleLogin}
            className="google-signin-button"
          >
            <svg width="18" height="18" viewBox="0 0 48 48">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              <path fill="none" d="M0 0h48v48H0z"/>
            </svg>
            Continue with Google
          </button>

          <button
            type="button"
            onClick={handleFacebookLogin}
            className="facebook-signin-button"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="#fff">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
            Continue with Facebook
          </button>
        </div>

        {/* Match Login Link */}
        <div style={{ textAlign: "center", marginBottom: "10px" }}>
          <button
            type="button"
            onClick={() => navigate("/login/match")}
            style={{
              background: "none",
              border: "none",
              color: "#007bff",
              textDecoration: "underline",
              cursor: "pointer",
              fontSize: "14px",
            }}
          >
            Or sign in for a match
          </button>
        </div>

        {/* Traditional Login Toggle */}
        {!showTraditionalLogin && (
          <div style={{ textAlign: "center", marginBottom: "20px" }}>
            <button
              type="button"
              onClick={() => setShowTraditionalLogin(true)}
              style={{
                background: "none",
                border: "none",
                color: "#007bff",
                textDecoration: "underline",
                cursor: "pointer",
                fontSize: "14px",
              }}
            >
              Or sign in with username and password
            </button>
          </div>
        )}

        {/* Traditional Login Form */}
        {showTraditionalLogin && (
          <Formik
            initialValues={{ username: "", password: "" }}
            validationSchema={loginSchema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting }) => (
              <Form className="login-form-fields">
                <div className="form-group">
                  <label htmlFor="username">Username:</label>
                  <Field
                    type="text"
                    name="username"
                    id="username"
                    className="form-control"
                  />
                  <ErrorMessage
                    name="username"
                    component="div"
                    className="error-message"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="password">Password:</label>
                  <Field
                    type="password"
                    name="password"
                    id="password"
                    className="form-control"
                  />
                  <ErrorMessage
                    name="password"
                    component="div"
                    className="error-message"
                  />
                </div>

                {error && <div className="error-message">Failed Login</div>}

                <button
                  type="submit"
                  disabled={isSubmitting || isButtonDisabled}
                  className="submit-button"
                >
                  Login
                </button>

                <div style={{ textAlign: "center", marginTop: "10px" }}>
                  <button
                    type="button"
                    onClick={() => setShowTraditionalLogin(false)}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#6c757d",
                      textDecoration: "underline",
                      cursor: "pointer",
                      fontSize: "12px",
                    }}
                  >
                    Back to Google login
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        )}

        {version && (
          <div className="version-info">
            <img style={{ width: "5vh" }} src={ajb} alt="AJB Logo" />
            <span>{version}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default Login;
